import { Button, DashboardContainer } from "components"
import React, { useState } from "react"
import { formatCurrencyNoSymbol } from "utils/currency"
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { Input, Tag } from "antd"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAppNotificationStore } from "stores"
import getIdFromKey from "utils/getIdFromKey"

import { mixPanelEvent } from "components/new-stuff/mix-panel/MixPanel"
import UnreversePayment from "./modals/UnreversePayment"
import NewModalWrapper from "components/new-stuff/ui/modals/NewModalWrapper"
import PersonAvatar from "components/new-stuff/ui/PersonAvatar"
import {
  paymentSchedulesService,
  PAYMENT_SCHEDULES_ENDPOINTS,
} from "services/paymentSchedulesService"
import { IPaymentReversalData, IPaymentReversalRes } from "./typings"
import type { TableColumnsType } from "antd"
import { onQueryError } from "utils/utils"
import { useBackendTableQuery } from "pages/dashboard/performance/components/useBackendTableQuery"
import { AppTable, TableActionSelect } from "components/new-stuff/app-table"

const Reversals = () => {
  let x: string = "0"
  const [paymentId, setPaymentId] = useState(getIdFromKey(x || ""))
  const { toast } = useAppNotificationStore()
  const queryClient = useQueryClient()

  const [countSelected, setCountSelected] = useState(0)
  const [selectedRowData, setSelectedRowData] = useState<any[] | null>([])
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [openUnreverseConfirmation, setOpenUnreverseConfirmation] =
    useState(false)

  const {
    isLoading: isPaymentLoading,
    isPreviousData,
    data: reversalData,
    search,
    handleSearchChange,
    tablePagination,
  } = useBackendTableQuery<IPaymentReversalRes>({
    queryKeyBase: [PAYMENT_SCHEDULES_ENDPOINTS.SELECT_PAYMENT_REVERSALS],
    queryFn: (params) => paymentSchedulesService.selectPaymentReversals(params),
    getTotal: (response) => response?.data?.totalDocs || 0,
    searchParamName: "searchParams",
    initialExtraParams: {
      payment_schedule_id: 0,
    },
  })

  const TableColumns: TableColumnsType<IPaymentReversalData> = [
    {
      title: "Beneficiary",
      dataIndex: "beneficiary",
      render: (_, record) => {
        const name =
          (record.payment_schedule_item_type_id ?? 0) > 1
            ? record.beneficiary_name
            : record.full_name || record.beneficiary_name
        const processed = String(record.payment_schedule_item_status_id) === "1"
        return (
          <div className="flex items-center gap-2">
            <span className="shrink-0">
              <PersonAvatar
                className="w-6 h-6 rounded-full"
                src={record.avatar_url ?? undefined}
                alt=""
              />
            </span>
            <div className="flex flex-col">
              <span
                className={`whitespace-nowrap font-avenir text-sm text-[#42526D] ${
                  processed ? "text-success800" : ""
                }`}
              >
                {name}
              </span>
              {(record.payment_schedule_item_type_id ?? 0) > 1 && (
                <Tag
                  color="processing"
                  className="w-[115px] text-xs font-medium"
                >
                  {record.payment_schedule_item_type_id === 3
                    ? "Pension Schedule"
                    : record.payment_schedule_item_type_id === 4
                    ? "NHF Schedule"
                    : record.payment_schedule_item_type_id === 2
                    ? "Tax Schedule"
                    : record.payment_schedule_item_type_id === 8
                    ? "EWA Repayment"
                    : ""}
                </Tag>
              )}
            </div>
          </div>
        )
      },
    },

    {
      title: "Description",
      dataIndex: "description",
      render: (description: string) => (
        <span
          className={`whitespace-nowrap font-avenir text-sm text-[#42526D] `}
        >
          {description}
        </span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (amount: number) => (
        <span
          className={`whitespace-nowrap font-avenir text-sm text-[#42526D] mp-mask`}
        >
          ₦ {formatCurrencyNoSymbol(amount)}
        </span>
      ),
    },

    {
      title: "Reversed Amount",
      dataIndex: "reversed_amount",
      render: (reversed_amount: number) => (
        <span
          className={`whitespace-nowrap font-avenir text-sm text-[#42526D] mp-mask`}
        >
          ₦ {formatCurrencyNoSymbol(reversed_amount)}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <TableActionSelect
          items={actionItems(
            Number(record.payment_schedule_item_status_id),
            record.payment_schedule_item_id,
            !!record.is_selected,
            record.is_active,
            String(record.payment_method_id),
            record.payment_schedule_item_type_id,
            record
          )}
        />
      ),
    },
  ]

  const actionItems = (
    id: number,
    paymentid: number,
    selected: boolean,
    active: boolean,
    payment_method_id: string,
    payment_schedule_item_type_id: number | undefined,
    payment: IPaymentReversalData
  ) => {
    let arrayOptions: any = []

    {
      active &&
        (arrayOptions = [
          ...arrayOptions,
          {
            key: "3",
            label: (
              <div
                onClick={() => {
                  handleSingleRowUnreverse(String(paymentid))
                }}
              >
                {" "}
                Unreverse
              </div>
            ),
          },
        ])
    }

    return arrayOptions
  }

  const invalidateQueries = () => {
    setPaymentId(getIdFromKey(x || ""))
    queryClient.invalidateQueries({
      queryKey: [PAYMENT_SCHEDULES_ENDPOINTS.SELECT_PAYMENT_REVERSALS],
    })
  }

  const { isLoading: isSubmittingUnreverseAll, mutate: submitUnreverseAll } =
    useMutation({
      mutationFn: async () => {
        const data = await paymentSchedulesService.deletePaymentReversals()

        return data
      },
      onSuccess: (data) => {
        data?.message && toast.success(data.message)
        setOpenDeleteModal(false)
        mixPanelEvent("pay-schedule-payout-unreverse-success")
        invalidateQueries()
      },
      onError: onQueryError,
    })

  const handleSingleRowUnreverse = (id: string) => {
    setPaymentId(id)
    setOpenUnreverseConfirmation(true)
  }
  const handSelectedRows = (
    selectedRowKeys: React.Key[],
    selectedRows: any
  ) => {
    setCountSelected(selectedRowKeys.length)
    setSelectedRowData(selectedRows)
  }

  return (
    <DashboardContainer>
      <>
        {/* The whole page used to be replaced by a full-height spinner, so the
            search and the toolbar vanished along with the rows. The table
            already receives `loading` and draws its own skeleton, so the page
            can render its shape immediately and fill in. */}
        {(
          <div className="relative">
            <div className=" bg-white rounded-5px mt-4  p-4 lg:p-[24px]">
              <div className=" flex items-center justify-between">
                <div className="w-[268px] h-9">
                  <Input
                    placeholder="Search"
                    prefix={
                      <MagnifyingGlassIcon className="w-4 h-4 text-neutral-300" />
                    }
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="font-avenir "
                  />
                </div>
                <Button
                  color="primary"
                  onClick={() => setOpenDeleteModal(true)}
                >
                  Delete
                </Button>
              </div>
              <div className="border mt-4 relative">
                <AppTable
                  columns={TableColumns}
                  dataSource={reversalData?.data?.docs || []}
                  rowKey="payment_reversal_id"
                  loading={isPaymentLoading || isPreviousData}
                  rowSelection={{
                    type: "checkbox",
                    onChange: (selectedRowKeys, selectedRows) => {
                      handSelectedRows(selectedRowKeys, selectedRows)
                    },
                  }}
                  className="text-[#42526D]"
                  pagination={tablePagination}
                />
                {/* <SelectedTable
                TableColumns={TableColumns}
                TableData={TableData}
                isPaymentLoading={isPaymentLoading}
                submitSelection={submitSelection}
                submitDeselection={submitDeselection}
                setAllSelectedRowSum={setAllSelectedRowSum}
              /> */}
              </div>
            </div>

            <UnreversePayment
              open={openUnreverseConfirmation}
              onClose={() => {
                setOpenUnreverseConfirmation(false)
                setPaymentId(getIdFromKey(x || ""))
              }}
              paymentId={paymentId}
              invalidateQueries={invalidateQueries}
              x={x}
            />
            <NewModalWrapper
              open={openDeleteModal}
              title="Delete Reversals"
              onCancel={() => setOpenDeleteModal(false)}
              onClose={() => setOpenDeleteModal(false)}
              footer={
                <div className="border py-5 px-6 flex gap-4  justify-end">
                  <Button
                    title="cancel"
                    color="secondary"
                    onClick={() => setOpenDeleteModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    submitting={isSubmittingUnreverseAll}
                    onClick={() => submitUnreverseAll()}
                  >
                    Confirm
                  </Button>
                </div>
              }
            >
              <div className="px-5 py-3">
                <p className=" text-neutral300 mb-5 text-base">
                  <span className="font-bold"> Note:</span> Only reversals that
                  are not in submitted payroll will be removed ....i.e payments
                  without reversed amount{" "}
                </p>
              </div>
            </NewModalWrapper>
          </div>
        )}
      </>
    </DashboardContainer>
  )
}
export default Reversals
interface ItransferDet {
  status: string
  msg: string
  pagi: string
  record: string
  data: string
}
